import {
  useActorUserCacheSync,
  useActorUserDataQueryOptions,
} from "@-/dev.improve.haniverse.lib.user";
import { AppLayoutContextProvider } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { AnalyticsPersonSync } from "@-/dev.improve.haniverse.routes.nav.app._components.analytics-person-sync";
import { StaticDatabaseWarmer } from "@-/dev.improve.haniverse.routes.nav.app._context.static-database-warmer";
import { MorphemeSelectionOverlay } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme-selection-overlay";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { StudyDataFailureNotifier } from "@-/dev.improve.haniverse.routes.nav.app._components.study-data-failure-notifier";
import { useDrainLearnQueue } from "@-/dev.improve.haniverse.routes.nav.app._lib.user-card.create";
import { useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import type { ReactNode } from "react";

export function AppLayoutSuspense({ children }: { children: ReactNode }) {
  // Hydrate actor-user state without suspending the app shell. Actor-user reads
  // synchronously return constructor defaults until the first query seeds MMKV.
  // Background initialization errors are reported by the query cache; they must
  // not remove the local welcome UI.
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  useQuery({ ...actorUserDataQueryOptions, throwOnError: false });

  // Keep the actor-user sync cache in step with the DB (local settings writes + remote sync).
  useActorUserCacheSync();

  // Graphemes a free learner queued after hitting the daily new-grapheme limit are enrolled as soon
  // as a day's allowance has room (or they upgrade). Waits for the initial pull so the queue and
  // today's count reflect every device.
  const isActorSyncPreparing = useIsActorSyncPreparing();
  useDrainLearnQueue({ enabled: !isActorSyncPreparing });

  return (
    <AppLayoutContextProvider>
      <StaticDatabaseWarmer />
      {/* Keeps analytics person/super properties current and reports level completions. */}
      <AnalyticsPersonSync />
      {/* A failed study database leaves the app usable; this tells the user only when they try
          to use study data, and routes them to support. */}
      <StudyDataFailureNotifier />
      {children}
      {/* Floats over all app screens; only renders while a morpheme multi-select is active. */}
      <MorphemeSelectionOverlay />
    </AppLayoutContextProvider>
  );
}
